
import { useRef, useState } from 'react';
import { cx } from '@client/shared/lib/cx.ts';
import { EventDetailCard, BlockDetailBody } from './EventDetailCard.tsx';
import { formatListingRange, formatListingTime } from './listing-zone-time.ts';
import { calendarLegendLabel } from '@client/shared/calendar/text.ts';
import styles from './ListingDetails.module.css';

export interface UnavailabilityBlockProps {
  reason: string | null;
  createdAt: string | null;
  zone: string | null;
  start: string;
  end: string;
  canEdit: boolean;
  onEditReason: (reason: string) => Promise<void>;
  onRemove: () => Promise<void>;
}

export function UnavailabilityBlock({
  reason,
  createdAt,
  zone,
  start,
  end,
  canEdit,
  onEditReason,
  onRemove,
}: UnavailabilityBlockProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const inlineLabel = reason && reason.trim() !== '' ? reason : calendarLegendLabel.blocked;
  const hasReason = Boolean(reason && reason.trim() !== '');

  const timeRange = formatListingRange(new Date(start), new Date(end), zone);
  const meta = createdAt ? (
    <>
      Set <span className="tnum">{formatListingTime(new Date(createdAt), zone)}</span>
    </>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.blockDetailTrigger}
        aria-label={hasReason ? `Blocked: ${inlineLabel}` : inlineLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={cx(styles.blockReasonInline, !hasReason && styles.blockReasonMuted)}>
          {inlineLabel}
        </span>
      </button>

      {open && (
        <EventDetailCard
          anchorRef={triggerRef}
          tone="blocked"
          eyebrow="Blocked time"
          ariaLabel="Blocked time"
          onClose={() => setOpen(false)}
        >
          <BlockDetailBody
            reason={reason}
            timeRange={timeRange}
            meta={meta}
            canEdit={canEdit}
            onEditReason={onEditReason}
            onRemove={onRemove}
            onClose={() => setOpen(false)}
          />
        </EventDetailCard>
      )}
    </>
  );
}
